import {
  i18nTranslate,
  isLATAM,
  currencyFormat,
  convertToBoolean,
  isEligibleToShowPVCVSV,
  getLocalStorage,
  stripCurrencySymbol,
} from 'src/utils'
import { customerContainer } from 'src/models'
import { getPaymentLabel } from './fixture'
import { APPConfig } from 'config/appConfig'

const formatPrice = (price, options = {}) => {
  // const { currencyCode = '', locale = '' } = price
  const finalCost = currencyFormat({
    // currency: currencyCode,
    price,
    ...options,
    // defaultLocale: locale,
  })
  // Replace non-breaking space with regular space for proper line breaking in print view
  return finalCost ? finalCost.replace?.(/\u00A0/g, ' ') : ''
}

const isToShowItemSV = () =>
  Boolean(
    isEligibleToShowPVCVSV({ accountType: getLocalStorage('accountType') })
  )

const getOrderProductTitle = () => [
  {
    title: i18nTranslate('printOrder.Code', 'Item'),
    key: 'item',
    isDefault: true,
  },
  {
    title: i18nTranslate('printOrder.description', 'Description'),
    key: 'name',
    isDefault: true,
  },
  {
    title: i18nTranslate('printOrder.Qty', 'Qty'),
    key: 'quantity',
    isDefault: true,
  },
  {
    title: i18nTranslate('printOrder.taxRate', 'Unit Tax'),
    key: 'tax',
    isDefault: true,
  },
  {
    title: i18nTranslate('printOrder.unitCost', 'Unit Cost'),
    key: 'unitCost',
    isDefault: true,
  },
  {
    title: i18nTranslate('printOrder.totalCost', 'Total Cost'),
    key: 'totalCost',
    isDefault: true,
  },
  {
    title: i18nTranslate('printOrder.totalSV', 'Total SV'),
    key: 'totalSV',
    isDefault: isToShowItemSV(),
  },
]
const getOrderDetails = orderItems => {
  const name = orderItems?.item?.itemInfo?.sku?.skuInfo?.name
  const quantity = orderItems?.item?.itemInfo?.quantity
  const tax =
    orderItems?.item?.itemInfo?.sku?.priceInfo?.[0]?.taxInfo?.tax || '0.00'
  const skuId = orderItems?.item?.itemInfo?.sku?.skuId
  const unitCost = orderItems?.item?.itemInfo?.sku?.priceInfo?.[0]?.salePrice
  const totalCost =
    orderItems?.item?.itemFinancials?.priceInfo?.[0]?.sale ||
    quantity * unitCost
  const totalSV =
    orderItems?.item?.itemFinancials?.priceFacets?.PV?.PVAfterDiscount ?? 0
  const data = [
    {
      key: 'item',
      value: skuId,
      isDefault: true,
    },
    {
      key: 'name',
      value: name,
      isDefault: true,
    },
    {
      key: 'quantity',
      value: quantity,
      isDefault: true,
    },
    {
      key: 'tax',
      value: formatPrice(tax),
      isDefault: true,
    },
    {
      key: 'unitCost',
      value: formatPrice(unitCost),
      isDefault: true,
    },
    {
      key: 'totalCost',
      value: formatPrice(totalCost),
      isDefault: true,
    },
    {
      key: 'totalSV',
      value: stripCurrencySymbol(formatPrice(totalSV, { isPVCV: true })),
      isDefault: isToShowItemSV(),
    },
  ]
  return data
}

const getOrderTotal = item => {
  const priceInfo = item?.orderInfo?.orderFinancials?.orderPriceInfo?.[0] || {}
  const isRegionTaxenable = item?.otherproperties?.isRegionTaxenable || false
  const isCategoryTaxenable =
    item?.otherproperties?.isCategoryTaxenable || false
  const fulfilmentCost =
    isCategoryTaxenable && isRegionTaxenable
      ? priceInfo?.fulfilmentPriceInfo?.fulfilmentSale || 0
      : priceInfo?.fulfilmentPriceInfo?.totalFulfilmentSale || 0
  const {
    tax = 0,
    totalSale = 0,
    discount = 0,
    sale = 0,
    otherChargesIncludingTax = 0,
    roundOff = 0,
  } = priceInfo
  const totalInclusiveTax = item?.otherproperties?.totalInclusiveTax || 0
  const taxValue =
    isRegionTaxenable && isCategoryTaxenable ? totalInclusiveTax : tax
  const estimatedShipping = fulfilmentCost?.toFixed(2)
  /**
   * @note
   * As, Backend support is not there, OrderTotal is calculated manually
   */
  const grandTotal = totalSale + tax + otherChargesIncludingTax + roundOff
  const loyaltyDiscount =
    item?.orderInfo?.orderDetails?.loyaltyDetails?.[0]?.value || 0
  const discountValue = Math.abs(discount) - loyaltyDiscount
  const totalDiscount = (discount < 0 ? '-' : '') + formatPrice(discountValue)
  const loyaltyDiscountPrice =
    (loyaltyDiscount > 0 ? '-' : '') + formatPrice(loyaltyDiscount)
  const locale = APPConfig?.getActiveAppConfig()?.defaultLocale
  const mexicoLocale = locale?.endsWith('MX')
  const isLATAMRegion = isLATAM()
  const enableOnlineReturnMFE =
    convertToBoolean(APPConfig?.getAppConfig()?.enableOnlineReturnMFE) || false
  const totalSV =
    item?.orderInfo?.orderFinancials?.priceFacets?.PV?.PVAfterDiscount ?? 0
  const isToShowSV = Boolean(
    isEligibleToShowPVCVSV({ accountType: getLocalStorage('accountType') }) &&
      (totalSV || totalSV === 0)
  )
  return [
    {
      label: i18nTranslate('printOrder.Subtotal', 'Subtotal:'),
      value: formatPrice(sale),
      isDefault: true,
    },
    {
      label: i18nTranslate(
        'printOrder.estimatedShipping',
        'Shipping & Handling:'
      ),
      value: formatPrice(estimatedShipping),
      isDefault: true,
    },
    {
      label: i18nTranslate('printOrder.discountTotal', 'Discount Total:'),
      value: totalDiscount,
      isDefault: true,
    },
    {
      label: getPaymentLabel('loyaltyDiscount'),
      value: loyaltyDiscountPrice,
      isDefault: true,
    },
    {
      label: i18nTranslate('printOrder.tax', 'Tax Total:'),
      value: formatPrice(taxValue),
      isDefault: true,
    },
    {
      label: mexicoLocale
        ? ''
        : isLATAMRegion || enableOnlineReturnMFE
          ? i18nTranslate('summary.otherCharges', 'Other charges')
          : i18nTranslate(
              'printOrder.mandatoryCORegulartoryFee',
              'Mandatory Colorado Delivery Fee:'
            ),
      value: mexicoLocale ? '' : formatPrice(otherChargesIncludingTax),
      isDefault: otherChargesIncludingTax > 0 ? true : false,
    },
    {
      label: i18nTranslate('printOrder.orderTotal', 'Order Total:'),
      value: formatPrice(grandTotal),
      isDefault: true,
    },
    {
      label: i18nTranslate('printOrder.totalSV', 'Total SV:'),
      value: stripCurrencySymbol(formatPrice(totalSV, { isPVCV: true })),
      isDefault: isToShowSV,
    },
  ]
}

const getPaymentValue = (item, PaymentType) => {
  const value =
    item?.payments?.find(pay => pay?.type?.toLowerCase() === PaymentType)
      ?.paymentItems?.[0]?.value || '0.0'

  return value
}

const getCreditCardNumber = (cardInfo, otherProperties) => {
  const hiddenCardNumber =
    otherProperties?.cardNumberLastFour || otherProperties?.cardNumber || ''
  const provider = cardInfo?.provider || otherProperties?.cardType || ''
  const cardNumber = cardInfo?.cardNumber || hiddenCardNumber || ''
  const slicedCardNumber = cardNumber
    ? cardNumber?.substring(cardNumber?.length - 4)
    : ''
  if (provider && slicedCardNumber) {
    return `****${slicedCardNumber}`
  }
  return ''
}

const getPaymentMethod = paymentInfo => {
  const { type = '', id = '', paymentItems = [] } = paymentInfo || {}
  const gpsDisplayName =
    paymentInfo?.paymentItems?.[0]?.otherproperties?.displayName
  const isDebitOrCreditCard =
    type?.toLowerCase() === 'creditcard' || type?.toLowerCase() === 'debitcard'
  const paymentLabel = getPaymentLabel(type?.toLowerCase()) || type
  let paymentTypeName = ''
  if (type.toLowerCase() === 'gps' && gpsDisplayName) {
    // show displayName as it is coming BE
    paymentTypeName =
      `${i18nTranslate(`paymentType.${gpsDisplayName}`, gpsDisplayName)}:` || ''
  } else {
    paymentTypeName =
      `${i18nTranslate(`paymentType.${type.toLowerCase()}`, type).toLowerCase()}:` ||
      ''
  }
  const { cardInfo = {}, otherproperties = {} } = paymentItems?.[0] || []
  const paymentValue = paymentItems?.[0]?.value || 0
  const cardNumber = getCreditCardNumber(cardInfo, otherproperties) || ''
  const cardDetails = paymentLabel + ' ' + cardNumber + ':'

  return {
    label: isDebitOrCreditCard ? cardDetails : paymentTypeName,
    value: formatPrice(paymentValue),
    isDefault: true,
    PaymentType: otherproperties?.paymentType,
  }
}

const getPayment = item => {
  const { payments = [] } = item || {}
  const otherPayments =
    payments?.filter(pay => pay?.type?.toLowerCase() !== 'storecredit') || []
  /**
   * @note
   * As, Backend support is not there, PaymentTotal is calculated manually
   * Payment Total includes total of all payments expect loyalty
   * As Loyalty is considered as Discount
   */
  let paymentTotal = 0
  try {
    if (payments) {
      payments?.forEach(payment => {
        paymentTotal += payment?.paymentItems
          ? payment?.paymentItems?.[0]?.value || 0
          : 0
      })
    }
  } catch (e) {
    console.log('catch payment error print invoice')
  }

  const paymentArray = [
    {
      label: getPaymentLabel('storecredit'),
      value: formatPrice(getPaymentValue(item, 'storecredit')),
      isDefault: true,
    },
  ]
  otherPayments?.forEach((paymentInfo, key) => {
    let paymentDetails = getPaymentMethod(paymentInfo)
    paymentArray.push(paymentDetails)
  })
  const orderTotalArray = [
    {
      label: i18nTranslate('printOrder.paymentTotal', 'Payment Total:'),
      value: formatPrice(paymentTotal),
      isDefault: true,
    },
  ]
  paymentArray.push(...orderTotalArray)
  return paymentArray
}

const formatDate = createdTime => {
  const dateObj = new Date(createdTime)
  let date = dateObj.getDate()
  date = date <= 9 ? `0${date}` : date
  let month = dateObj.getMonth() + 1
  month = month <= 9 ? `0${month}` : month

  // return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`
  return `${dateObj.getFullYear()}-${month}-${date}`
}

const getOrderInfo = item => {
  const orderCreatedTime = item?.orderInfo?.orderDetails?.createdTime || ''
  const orderDate = formatDate(orderCreatedTime)
  const orderID = item?.orderInfo?.orderDetails?.id || ''
  const trackingId =
    item?.orderInfo?.orderDetails?.orderItems?.[0]?.shippingInfo
      ?.shippingDetails?.trackingId || ''
  return [
    {
      title: i18nTranslate('printOrder.orderDate', 'Order Date:'),
      value: orderDate,
      isDefault: true,
    },
    {
      title: i18nTranslate('printOrder.orderID', 'Order ID:'),
      value: orderID,
      isDefault: true,
    },
    {
      title: i18nTranslate('printOrder.customerId', 'Customer ID:'),
      value:
        customerContainer?.profileResponse?.accounts?.referenceAttribute ||
        item?.customer?.userTracking?.accountId ||
        '',
      isDefault: true,
    },
    trackingId && {
      title: i18nTranslate('printOrder.trackingId', 'Tracking ID:'),
      value: trackingId,
      isDefault: true,
    },
  ]
}

const getPrintOrderDetails = props => {
  const item = props
  const { payments = [] } = props || {}
  const cardDetails =
    payments?.find(item => {
      const type = item?.type?.toLowerCase()
      return type == 'creditcard' || type == 'debitcard'
    }) || {}
  const address =
    item?.orderInfo?.orderDetails?.orderItems?.[0]?.shippingInfo || {}
  const shippingInfo = address?.shippingAddress || {}
  const shippingMethod = address?.shippingMethod || ''
  const billingInfo = cardDetails?.billingAddress || shippingInfo || {}
  const list = item?.orderInfo?.orderDetails?.orderItems || []

  return [
    {
      orderInfo: getOrderInfo(item),
      shippingAddress: { shippingInfo, isDefault: true, shippingMethod },
      // billingAddress: { billingInfo, isDefault: true },
      orderDetails: {
        orderProductTitle: getOrderProductTitle(),
        orderProductInfo: list && list.map(getOrderDetails),
      },
      orderTotalInfo: getOrderTotal(item),
      payments: getPayment(item),
    },
  ]
}

export { getPrintOrderDetails }
